import { useState } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import type { PayrollRunWithDetails } from '@/services/payroll/payrollHistoryService';

interface PayrollRollbackDialogProps {
  run: PayrollRunWithDetails | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (runId: string, reason: string) => void;
  isRollingBack: boolean;
}

export function PayrollRollbackDialog({
  run,
  open,
  onOpenChange,
  onConfirm,
  isRollingBack,
}: PayrollRollbackDialogProps) {
  const [reason, setReason] = useState('');

  const handleConfirm = () => {
    if (!run || !reason.trim()) return;
    onConfirm(run.id, reason.trim());
  };

  const handleClose = () => {
    if (!isRollingBack) {
      setReason('');
      onOpenChange(false);
    }
  };

  if (!run) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Rollback Payroll Run
          </DialogTitle>
          <DialogDescription>
            This action will permanently delete all payslips for run #{run.run_number}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Warning:</strong> This action cannot be undone. All payslips and line items 
              associated with this payroll run will be permanently deleted.
            </AlertDescription>
          </Alert>

          <Separator />

          <div className="space-y-2">
            <h4 className="font-medium text-sm">Run Details</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="text-muted-foreground">Run Number:</div>
              <div className="font-medium">{run.run_number}</div>
              
              <div className="text-muted-foreground">Payslips Generated:</div>
              <div className="font-medium">{run.payslips_generated || 0}</div>
              
              <div className="text-muted-foreground">Period:</div>
              <div className="font-medium">
                {new Date(run.start_date).toLocaleDateString()} - {new Date(run.end_date).toLocaleDateString()}
              </div>
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <Label htmlFor="rollback-reason" className="text-destructive">
              Reason for Rollback <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="rollback-reason"
              placeholder="Please provide a detailed reason for rolling back this payroll run (e.g., incorrect rates applied, duplicate entries, etc.)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              disabled={isRollingBack}
              className="resize-none"
            />
            {!reason.trim() && (
              <p className="text-xs text-muted-foreground">
                A reason is required to proceed with the rollback
              </p>
            )}
          </div>

          <Alert>
            <AlertDescription className="text-xs">
              After rollback, you can regenerate payslips for this period if needed. 
              The rollback will be logged in the audit trail.
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isRollingBack}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!reason.trim() || isRollingBack}
          >
            {isRollingBack ? (
              <>
                <RotateCcw className="mr-2 h-4 w-4 animate-spin" />
                Rolling Back...
              </>
            ) : (
              <>
                <RotateCcw className="mr-2 h-4 w-4" />
                Confirm Rollback
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
